/**
 * Inputs: event + optional linked booking. Outputs: the plan status the family
 * sees, timing confirmation, and a save patch that does not drop imported fields.
 * Booking status (booked) and meeting/time confidence (provisional) stay separate.
 * Transfer pickup/arrival times persist for after-landing and when-ready modes so
 * the diary timeline can order them; those modes stay unconfirmed clock times.
 */

import type { Booking, TripEvent } from "./schema.ts";
import { clockHm } from "./time.ts";

export type PlanStatus = "idea" | "planned" | "booked" | "cancelled";
export type TimingMode = "after_arrival" | "when_ready" | "fixed" | "flexible";

const STATUS_ONLY_NOTE = /^(provisional|booked|planned|idea|cancelled|confirmed)$/i;

export function isProvisionalConfidence(value?: string | null): boolean {
  return Boolean(value?.includes("provisional"));
}

export function planStatusFromBookingStatus(status?: string | null): PlanStatus | undefined {
  if (!status) return undefined;
  if (status === "cancelled" || status === "idea" || status === "planned") return status;
  if (status.startsWith("booked")) return "booked";
  return undefined;
}

export function resolvedPlanStatus(
  event: Pick<TripEvent, "planStatus" | "bookingId">,
  booking?: Pick<Booking, "bookingStatus"> | null,
): PlanStatus {
  if (event.planStatus) return event.planStatus;
  const fromBooking = planStatusFromBookingStatus(booking?.bookingStatus);
  if (fromBooking) return fromBooking;
  if (event.bookingId) return "booked";
  return "planned";
}

export function displayBookingStatus(
  event: Pick<TripEvent, "planStatus" | "bookingId">,
  booking?: Pick<Booking, "bookingStatus"> | null,
): string {
  if (booking?.bookingStatus) {
    const plan = event.planStatus;
    if (plan === "cancelled" && booking.bookingStatus !== "cancelled") return plan;
    if (plan === "idea" || plan === "planned") return plan;
    return booking.bookingStatus;
  }
  return resolvedPlanStatus(event, booking);
}

export function bookingStatusAfterPlanChange(
  current: Booking["bookingStatus"],
  plan: PlanStatus,
): Booking["bookingStatus"] {
  if (plan === "cancelled") return "cancelled";
  if (plan === "idea") return "idea";
  if (plan === "planned") {
    if (current.startsWith("booked")) return "planned";
    if (current === "cancelled") return "planned";
    return current === "idea" ? "planned" : current;
  }
  if (current.startsWith("booked")) return current;
  return "booked_owner_reported";
}

export function timingModeFromEvent(event: TripEvent): TimingMode {
  if (event.pickupMode === "when_ready" || event.pickupMode === "after_arrival") {
    return event.pickupMode;
  }
  if (event.pickupMode === "fixed" && !isProvisionalConfidence(event.startConfidence)) {
    return "fixed";
  }
  if (isProvisionalConfidence(event.startConfidence) || isProvisionalConfidence(event.timeConfidence)) {
    return "flexible";
  }
  if (event.startLocal || event.plannedDeparture) return "fixed";
  if (event.pickupMode === "fixed") return "fixed";
  return "flexible";
}

export function workingTimeFromEvent(event: TripEvent): string {
  return clockHm(event.startLocal ?? event.plannedDeparture) ?? "";
}

export function workingArrivalFromEvent(event: TripEvent): string {
  return clockHm(event.plannedArrival ?? event.endLocal) ?? "";
}

export function timingConfidenceMessage(event: TripEvent): string | null {
  const timeProv = isProvisionalConfidence(event.startConfidence) || isProvisionalConfidence(event.timeConfidence);
  const meetProv = isProvisionalConfidence(event.meetingConfidence);
  if (event.startConfidence === "provisional_generic_webpage") {
    if (event.startLocal) {
      return `Meeting time still to confirm. ${event.startLocal} came from a generic screenshot, not the custom-tour confirmation.`;
    }
    return "Meeting time still to confirm. It came from a generic screenshot, not the custom-tour confirmation.";
  }
  if (timeProv && meetProv) return "Time and exact meeting point still to confirm.";
  if (timeProv) return "Time still to confirm.";
  if (meetProv) return "Exact meeting point still to confirm.";
  return null;
}

export function visibleEventNotes(notes: string[] | undefined): string[] {
  return (notes ?? []).filter((note, index) => {
    const trimmed = note.trim();
    if (!trimmed) return false;
    if (index === 0 && STATUS_ONLY_NOTE.test(trimmed)) return false;
    return true;
  });
}

export function composeNotesFromEvent(event: TripEvent | undefined): { current: string; later: string[] } {
  const notes = event?.notes ?? [];
  if (notes[0] && STATUS_ONLY_NOTE.test(notes[0].trim())) {
    return { current: notes[1] ?? "", later: notes.slice(2) };
  }
  return { current: notes[0] ?? "", later: notes.slice(1) };
}

export type EventPlanPatch = {
  id: string;
  kind: TripEvent["kind"];
  title: string;
  date: string;
  timezone: string;
  status: PlanStatus;
  timing: TimingMode;
  time: string;
  arrivalTime?: string;
  meetingPlaceId: string | null;
  fromPlaceId: string | null;
  fromAirport: string | null;
  toPlaceId: string | null;
  pickupInstruction: string | null;
  transferMethod: TripEvent["transferMethod"];
  notes: string[];
  bookingId: string | null;
  linkedFlightId: string | null;
  sortIndex?: number;
};

export function applyEventPlanEdits(existing: TripEvent | undefined, patch: EventPlanPatch): TripEvent {
  const flexibleTime = patch.timing === "flexible" || patch.timing === "when_ready" || patch.timing === "after_arrival";
  const workingTime = clockHm(patch.time)
    ?? clockHm(existing?.startLocal)
    ?? clockHm(existing?.plannedDeparture);
  const arrivalTime = patch.arrivalTime === undefined
    ? clockHm(existing?.plannedArrival ?? existing?.endLocal)
    : clockHm(patch.arrivalTime);
  let startConfidence = existing?.startConfidence ?? null;
  if (patch.timing === "fixed" && workingTime) {
    startConfidence = isProvisionalConfidence(startConfidence) ? "owner_confirmed" : startConfidence;
  } else if (patch.timing === "flexible") {
    if (!startConfidence || startConfidence === "owner_confirmed") {
      startConfidence = workingTime ? "provisional" : startConfidence;
    }
  }

  return {
    id: patch.id,
    kind: patch.kind,
    title: patch.title,
    date: patch.date,
    timezone: patch.timezone,
    startLocal: workingTime,
    plannedDeparture: patch.kind === "transfer" ? workingTime : existing?.plannedDeparture ?? null,
    plannedArrival: patch.kind === "transfer" ? arrivalTime : existing?.plannedArrival ?? null,
    endLocal: patch.kind === "transfer" ? arrivalTime : existing?.endLocal ?? null,
    flexible: flexibleTime,
    meetingPlaceId: patch.kind === "excursion" ? patch.meetingPlaceId : existing?.meetingPlaceId ?? null,
    fromPlaceId: patch.kind === "transfer" ? patch.fromPlaceId : existing?.fromPlaceId ?? null,
    fromAirport: patch.kind === "transfer" ? patch.fromAirport : existing?.fromAirport ?? null,
    toPlaceId: patch.kind === "transfer"
      ? patch.toPlaceId
      : patch.kind === "excursion"
        ? patch.toPlaceId ?? existing?.toPlaceId ?? null
        : existing?.toPlaceId ?? null,
    pickupInstruction: patch.kind === "transfer" ? patch.pickupInstruction : existing?.pickupInstruction ?? null,
    pickupMode: patch.kind === "transfer" ? patch.timing : existing?.pickupMode ?? null,
    transferMethod: patch.kind === "transfer" ? patch.transferMethod : existing?.transferMethod ?? null,
    planStatus: patch.status,
    notes: patch.notes,
    bookingId: patch.bookingId,
    linkedFlightId: patch.linkedFlightId,
    sortIndex: patch.sortIndex ?? existing?.sortIndex,
    startConfidence,
    endConfidence: existing?.endConfidence ?? null,
    timeConfidence: existing?.timeConfidence ?? null,
    dateConfidence: existing && existing.date !== patch.date
      ? "owner_confirmed"
      : existing?.dateConfidence ?? null,
    meetingConfidence: existing?.meetingConfidence ?? null,
    meetingNote: existing?.meetingNote ?? null,
    includesBoat: existing?.includesBoat ?? null,
    stops: existing?.stops ?? [],
    sourceIds: existing?.sourceIds ?? [],
    archived: false,
    flightId: existing?.flightId ?? null,
    stayId: existing?.stayId ?? null,
  };
}

export function withSyncedBookingStatus(
  bookings: Booking[],
  bookingId: string | null | undefined,
  plan: PlanStatus,
): Booking[] {
  if (!bookingId) return bookings;
  return bookings.map((booking) => (
    booking.id === bookingId
      ? { ...booking, bookingStatus: bookingStatusAfterPlanChange(booking.bookingStatus, plan) }
      : booking
  ));
}
